import { useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { GlobalState } from "context/GlobalContext";
import axios from "axios";

const STORE_MASTER_UPLOAD_ENDPOINT = "/api/pos_reco/upload_store_master";

const uploadModes = [
	{ label: "Overwrite", value: "overwrite" },
	{ label: "Append", value: "append" },
];

const StoreMasterUploadDialog = ({ open, onClose, onUploadSuccess }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [file, setFile] = useState(null);
	const [uploadMode, setUploadMode] = useState("");
	const [loading, setLoading] = useState(false);

	const handleDialogClose = () => {
		if (loading) return;
		setFile(null);
		setUploadMode("");
		onClose();
	};

	const handleFileChange = (event) => {
		setFile(event.target.files?.[0] || null);
	};

	const handleUpload = async () => {
		if (!file || !uploadMode) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select a file and upload mode",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoading(true);
		try {
			const formData = new FormData();
			formData.append("file", file);
			formData.append("mode", uploadMode);

			await axios.post(STORE_MASTER_UPLOAD_ENDPOINT, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Store master ${uploadMode === "overwrite" ? "replaced" : "updated"} successfully`,
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});

			onUploadSuccess?.();
			handleDialogClose();
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable to upload store master file",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
			<DialogTitle>Upload Store Master</DialogTitle>
			<DialogContent>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
					<Box>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							Select the store master file and choose whether to overwrite the current data or append new rows.
						</Typography>
						<Button
							component="label"
							variant="outlined"
							startIcon={<UploadFileRoundedIcon />}
							sx={{ textTransform: "none", justifyContent: "flex-start", width: "100%", py: 1.3 }}>
							{file ? file.name : "Choose file"}
							<input type="file" hidden onChange={handleFileChange} accept=".xlsx,.xls,.csv" />
						</Button>
					</Box>

					<FormControl fullWidth size="small">
						<InputLabel id="store-master-upload-mode-label">Upload Mode</InputLabel>
						<Select
							labelId="store-master-upload-mode-label"
							value={uploadMode}
							label="Upload Mode"
							onChange={(event) => setUploadMode(event.target.value)}>
							{uploadModes.map((mode) => (
								<MenuItem key={mode.value} value={mode.value}>
									{mode.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2.5 }}>
				<Button onClick={handleDialogClose} variant="outlined" disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleUpload}
					variant="contained"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <UploadFileRoundedIcon />}>
					{loading ? "Uploading..." : "Upload"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default StoreMasterUploadDialog;
